import React, { ReactElement, ReactNode } from 'react'
import { Provider } from 'react-redux'
import { StyledEngineProvider, ThemeProvider } from '@mui/material'
import { NextPage } from 'next'
import type { AppProps } from 'next/app'
import { SessionProvider } from 'next-auth/react'
import { appWithTranslation } from 'next-i18next'
import { NextStep, NextStepProvider } from 'nextstepjs'
import { FlagProvider, UnleashClient } from '@unleash/proxy-client-react'

import { store } from '#/app/store/store'
import { Error } from '#/shared'
import { LowBalanceOfferTrigger, PaymentSuccessToast } from '#/features/low-balance-offer'
import { TourManager, getTourSteps, TourCard, setTourCompleted } from '#/features/nextstep-tour'
import { pingFangFont } from '#/shared/lib/constants/font/font'
import { getDeviceType } from '#/shared/lib/helpers'
import { useBlockTelegram } from '#/shared/lib/hooks/use-block-telegram'
import { useEnv } from '#/shared/lib/hooks/use-env'
import { CookieConsent } from '#/features/cookie-consent/ui/cookie-consent'
import { Providers } from '#/widgets/providers'

import { setupAxios } from '#/shared/api/axios-interceptors'

import ErrorBoundary from './error-boundary'

import '#/app/styles/globals.css'
import '#/app/styles/styles-pages/system.scss'

setupAxios()

export type NextPageWithLayout<P = {}, DP = {}, IP = P> = NextPage<P, IP> & {
	getLayout?: (page: ReactElement) => ReactNode
	dynamicProps?: DP
}

export interface AppPropsWithLayout extends AppProps {
	Component: NextPageWithLayout
}

function AppContent({
	Component,
	pageProps,
	refetchInterval,
}: {
	Component: NextPageWithLayout
	pageProps: any
	refetchInterval?: number
}) {
	const handleTourComplete = () => {
		setTourCompleted()
	}

	const handleTourSkip = () => {
		setTourCompleted()
	}

	const tourSteps = React.useMemo(() => getTourSteps(getDeviceType()), [getDeviceType()])

	return (
		<NextStep 
			steps={tourSteps} 
			shadowOpacity='0.6'
			cardComponent={TourCard}
			onComplete={handleTourComplete}
			onSkip={handleTourSkip}>
			<div>
				<ErrorBoundary>
					<StyledEngineProvider injectFirst>
						<Provider store={store}>
							<SessionProvider session={pageProps.session} refetchInterval={refetchInterval ?? 0}>
								<TourManager>
									<ThemeProvider theme={pingFangFont}>
										<Providers>
											{Component.getLayout ? Component.getLayout(<Component {...pageProps} />) : <Component {...pageProps} />}
											<CookieConsent />
											<LowBalanceOfferTrigger />
											<PaymentSuccessToast />
											<Error />
										</Providers> 
									</ThemeProvider>
								</TourManager>
							</SessionProvider>
						</Provider>
					</StyledEngineProvider>
				</ErrorBoundary>
			</div>
		</NextStep>
	)
}

function App({ Component, pageProps: { session, ...pageProps } }: AppPropsWithLayout) {
	useBlockTelegram()
	const { env, loading: envLoading } = useEnv()

	const unleashUrl = env?.NEXT_PUBLIC_UNLEASH_URL
	const unleashClientKey = env?.NEXT_PUBLIC_UNLEASH_CLIENT_KEY
	const unleashAppName = env?.NEXT_PUBLIC_UNLEASH_APP_NAME

	const unleashClient = React.useMemo(() => {
		if (envLoading) return null
		if (!unleashUrl || !unleashClientKey || !unleashAppName) return null
		return new UnleashClient({
			url: unleashUrl,
			clientKey: unleashClientKey,
			appName: unleashAppName,
		})
	}, [envLoading, unleashAppName, unleashClientKey, unleashUrl])

	React.useEffect(() => {
		if (!unleashClient) return
		unleashClient.start()
		return () => {
			unleashClient.stop?.()
		}
	}, [unleashClient])

	if (envLoading) {
		return null
	}

	return (
		<>
			{unleashClient ? (
				<FlagProvider unleashClient={unleashClient} startClient={false}>
					<NextStepProvider>
						<AppContent
							Component={Component}
							pageProps={{ session, ...pageProps }}
							refetchInterval={Number(env?.NEXT_PUBLIC_REFETCH_INTERVAL) || undefined}
						/>
					</NextStepProvider>
				</FlagProvider>
			) : (
				<NextStepProvider>
					<AppContent
						Component={Component}
						pageProps={{ session, ...pageProps }}
						refetchInterval={Number(env?.NEXT_PUBLIC_REFETCH_INTERVAL) || undefined}
					/>
				</NextStepProvider>
			)}
		</>
	)
}

export default appWithTranslation(App)
